import React, { useState, useEffect } from 'react';
import { Sun, Moon, Calendar, Globe, Shield, LogOut } from 'lucide-react';

export default function Header({ 
  currentTenant, 
  shift, 
  onToggleShift, 
  selectedDate, 
  onChangeDate, 
  currentRole, 
  lang = 'mr',
  onToggleLang,
  onLogout
}) {
  const isMr = lang === 'mr';
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <header className="no-print" style={{
      background: '#ffffff',
      borderBottom: '1px solid #e2e8f0',
      position: 'sticky',
      top: 0,
      zIndex: 30,
      boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
    }}>
      <div style={{
        maxWidth: 1440,
        margin: '0 auto',
        padding: isMobile ? '0.45rem 0.75rem' : '0.55rem 1.25rem',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'center',
        justifyContent: 'space-between',
        gap: isMobile ? '0.45rem' : '0.75rem'
      }}>
        
        {/* ROW 1: Tenant Branding & Global Actions (Language & Logout) */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.65rem' }}>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <img 
              src="/milkpro_logo.jpg" 
              alt="MilkPro Mitra" 
              style={{ width: isMobile ? 32 : 36, height: isMobile ? 32 : 36, borderRadius: 8, objectFit: 'contain', border: '1px solid #e2e8f0' }}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
            <div>
              <div style={{ fontWeight: 900, fontSize: isMobile ? '0.88rem' : '1rem', color: '#0f172a', lineHeight: 1.2 }}>
                {currentRole === 'DEVELOPER' 
                  ? 'MilkPro Mitra - SaaS Developer Portal'
                  : (isMr ? currentTenant?.brandMarathi : currentTenant?.brandEnglish)}
              </div>
              <div style={{ fontSize: '0.66rem', color: '#64748b', marginTop: 1 }}>
                {currentRole === 'DEVELOPER'
                  ? 'Landlord DB: milkpromitra_main'
                  : `${currentTenant?.city || 'सातारा'} • ${isMr ? (currentTenant?.plan?.name_mr || currentTenant?.plan?.code || 'ॲडव्हान्स') : (currentTenant?.plan?.name_en || currentTenant?.plan?.code || 'Advanced')}`}
              </div>
            </div>
          </div>

          {/* Quick Header Actions on Right */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            {/* Language Switcher */}
            <button
              onClick={() => onToggleLang(isMr ? 'en' : 'mr')}
              style={{
                display: 'flex', alignItems: 'center', gap: 3,
                padding: '0.25rem 0.5rem', borderRadius: 6,
                border: '1px solid #cbd5e1', background: '#f8fafc',
                fontSize: '0.72rem', fontWeight: 800, cursor: 'pointer',
                color: '#0284c7'
              }}
            >
              <Globe size={12} />
              <span>{isMr ? 'English' : 'मराठी'}</span>
            </button>

            {/* Logout button */}
            {onLogout && (
              <button
                onClick={onLogout}
                title={isMr ? 'लॉगआउट करा' : 'Logout'}
                style={{
                  display: 'flex', alignItems: 'center', gap: 3,
                  background: '#fef2f2',
                  color: '#dc2626',
                  border: '1px solid #fecaca',
                  borderRadius: 6,
                  padding: '0.25rem 0.5rem',
                  fontSize: '0.72rem',
                  fontWeight: 800,
                  cursor: 'pointer'
                }}
              >
                <LogOut size={12} />
                <span>{isMr ? 'लॉगआउट' : 'Logout'}</span>
              </button>
            )}
          </div>

        </div>

        {/* ROW 2: Date, Shift, and Role Badge (Compact Horizontal Strip on Mobile) */}
        {currentRole !== 'DEVELOPER' && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: isMobile ? 'space-between' : 'flex-end',
            gap: '0.45rem',
            background: isMobile ? '#f8fafc' : 'transparent',
            padding: isMobile ? '4px 6px' : 0,
            borderRadius: isMobile ? 8 : 0,
            border: isMobile ? '1px solid #e2e8f0' : 'none'
          }}>
            {/* 1. Date Picker */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              background: '#ffffff',
              border: '1px solid #cbd5e1',
              padding: '0.25rem 0.45rem',
              borderRadius: 6,
              flex: isMobile ? 1 : undefined
            }}>
              <Calendar size={13} color="#0284c7" />
              <input
                type="date"
                value={selectedDate}
                onChange={e => onChangeDate(e.target.value)}
                style={{
                  border: 'none',
                  background: 'transparent',
                  fontSize: '0.74rem',
                  fontWeight: 700,
                  color: '#0f172a',
                  outline: 'none',
                  width: '100%'
                }}
              />
            </div>

            {/* 2. Shift Toggle */}
            <button
              onClick={() => onToggleShift(shift === 'Morning' ? 'Evening' : 'Morning')}
              style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '0.25rem 0.55rem', borderRadius: 6,
                fontSize: '0.74rem', fontWeight: 800, cursor: 'pointer',
                border: '1px solid ' + (shift === 'Morning' ? '#fde68a' : '#c7d2fe'),
                background: shift === 'Morning' ? '#fef3c7' : '#e0e7ff',
                color: shift === 'Morning' ? '#92400e' : '#3730a3',
                whiteSpace: 'nowrap'
              }}
            >
              {shift === 'Morning' ? <Sun size={13} color="#d97706" /> : <Moon size={13} color="#4f46e5" />}
              <span>{isMr ? (shift === 'Morning' ? 'सकाळ' : 'संध्याकाळ') : shift}</span>
            </button>

            {/* 3. Role Badge */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              padding: '0.25rem 0.5rem',
              borderRadius: 6,
              fontSize: '0.72rem',
              fontWeight: 800,
              color: '#334155',
              whiteSpace: 'nowrap'
            }}>
              <Shield size={12} color="#64748b" />
              <span>
                {currentRole === 'ADMIN' 
                  ? (isMr ? 'डेअरी मालक' : 'Admin') 
                  : (currentRole === 'DELIVERY' ? (isMr ? 'डिलिव्हरी' : 'Delivery') : currentRole)}
              </span>
            </div>

          </div>
        )}

      </div>
    </header>
  );
}
